import { useEffect, useRef, useState, Dispatch, SetStateAction, useCallback } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../../ui/toast';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import {
  JobDescription,
  JobPostingTabData,
} from '../../../../../../../utils/types/JobOpenings';
import {
  getJobPost,
  saveTranslation,
  updateJobPostUpload,
  createJobPostUpload,
} from '../../../../../../../utils/api_service/JobPosting';
import { toISODateString } from '../../../../../../../utils/helpers/dateUtils';
import defaultEmailObjectTemplate from '../email_template.json';

const defaultJsonTemplate = JSON.stringify(defaultEmailObjectTemplate);

export interface UseJobPostDetailsParams {
  jobPostingData: JobPostingTabData;
  setJobPostingData: Dispatch<SetStateAction<JobPostingTabData>>;
  jobDescription: JobDescription;
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  orgCompanyDetails: string;
  orgPerksAndBenefits: string;
  jobPostId?: string;
  setJobPostId: Dispatch<SetStateAction<string>>;
  setStages: (value: any) => void;
  setOrgPerksAndBenefits: (value: (((prevState: string) => string) | string)) => void,
  setOrgCompanyDetails: (value: (((prevState: string) => string) | string)) => void,
  orgNames: string[]
}

export const useJobPostDetails = (params: UseJobPostDetailsParams) => {
  const {
    jobPostingData,
    setJobPostingData,
    jobDescription,
    orgCompanyDetails,
    orgPerksAndBenefits,
    jobPostId,
    setJobPostId,
    setStages,
    setOrgPerksAndBenefits,
    setOrgCompanyDetails,
    orgNames,
  } = params;

  const { t } = useTranslation(NS.adminPages);
  const { id } = useParams<{ id: string }>();

  const resolvedPostId = jobPostId || jobPostingData.id;

  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [liveHtml, setLiveHtml] = useState<string>('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [variables] = useState<{ name: string; variable: string }[]>([
    {
      name: 'Responsibility',
      variable: jobDescription.responsibilities
        ? `<ul>${jobDescription.responsibilities
          .map((responsibility) => `<li>${responsibility}</li>`)
          .join('')}</ul>`
        : '',
    },
    {
      name: 'Required Skills',
      variable: jobDescription.mandatorySkills
        ? `<ul>${jobDescription.mandatorySkills
          .map((skill) => `<li>${skill}</li>`)
          .join('')}</ul>`
        : '',
    },
    {
      name: 'Desired Skills',
      variable: jobDescription.desirableSkills
        ? `<ul>${jobDescription.desirableSkills
          .map((skill) => `<li>${skill}</li>`)
          .join('')}</ul>`
        : '',
    },
    {
      name: 'Perks and Benefits',
      variable: orgPerksAndBenefits
        ? orgPerksAndBenefits
          .split(/\n\s*\n/)
          .map((paragraph) => `<p>${paragraph.trim()}</p>`)
          .join('')
        : '',
    },
    {
      name: 'Company Details',
      variable: orgCompanyDetails
        ? orgCompanyDetails
          .split(/\n\s*\n/)
          .map((paragraph) => `<p>${paragraph.trim()}</p>`)
          .join('')
        : '',
    },
    {
      name: 'Behavioural Attributes',
      variable: jobDescription.behavioralAttributes
        ? `<ul>${jobDescription.behavioralAttributes
          .map((skill) => `<li>${skill}</li>`)
          .join('')}</ul>`
        : '',
    },
  ]);

  const [initialEditorState, setInitialEditorState] = useState<any>(null);
  const [isImported, setIsImported] = useState<boolean>(false);
  const [activeJsonTemplate, setActiveJsonTemplate] = useState<string>(
    jobPostingData.jsonTemplate || defaultJsonTemplate,
  );
  const [editorKey, setEditorKey] = useState(0);
  const [autoSaveLang, setAutoSaveLang] = useState<string | null>(null);
  const autoSavingRef = useRef(false);

  // Sync editor live updates to liveHtml state
  useEffect(() => {
    if (!editorInstance) return;
    setLiveHtml(editorInstance.getHTML());

    const handleUpdate = () => {
      setLiveHtml(editorInstance.getHTML());
    };

    editorInstance.on('update', handleUpdate);
    return () => {
      editorInstance.off('update', handleUpdate);
    };
  }, [editorInstance]);

  // English translation loader effect
  useEffect(() => {
    if (!jobPostingData.id) return;
    const hasTranslations =
      (jobPostingData.availableLanguages ?? []).filter((l) => l !== 'en').length > 0;
    if (!hasTranslations) return;

    const loadEnglish = async () => {
      try {
        const res = await getJobPost(resolvedPostId, 'en');
        if (!res.ok) return;
        const data = await res.json();
        const jobPost = data.jobPosting || data;
        const engJson = jobPost.jsonTemplate || defaultJsonTemplate;
        const engHtml = jobPost.htmlTemplate || '';

        setActiveJsonTemplate(engJson);
        setEditorKey((k) => k + 1);

        setJobPostingData((prev) => ({
          ...prev,
          htmlTemplate: engHtml,
          jsonTemplate: engJson,
          language: undefined,
        }));
      } catch (err) {
        console.error('loadEnglish error', err);
      }
    };

    loadEnglish();
  }, [jobPostingData.id, resolvedPostId, setJobPostingData]);

  const insertResponsibilities = useCallback(() => {
    if (
      editorInstance &&
      jobDescription.responsibilities &&
      jobDescription.responsibilities.length > 0
    ) {
      const doc = editorInstance.state.doc.toJSON();
      const responsibilitiesIndex = doc.content.findIndex(
        (node: any) =>
          node.type === 'paragraph' &&
          node.content?.[0]?.text === 'Responsibilities' &&
          node.content?.[0]?.marks?.some((mark: any) => mark.type === 'bold'),
      );

      if (responsibilitiesIndex >= 0) {
        let position = 0;
        for (let i = 0; i <= responsibilitiesIndex; i++) {
          const node = editorInstance.state.doc.child(i);
          position += node.nodeSize;
        }

        const bulletList = {
          type: 'bulletList',
          content: jobDescription.responsibilities.map((item) => ({
            type: 'listItem',
            content: [
              { type: 'paragraph', content: [{ type: 'text', text: item }] },
            ],
          })),
        };

        editorInstance.chain().focus().insertContentAt(position, bulletList).run();
      }
    }
  }, [editorInstance, jobDescription.responsibilities]);

  const insertRequiredSkills = useCallback(() => {
    if (
      editorInstance &&
      jobDescription.mandatorySkills &&
      jobDescription.mandatorySkills.length > 0
    ) {
      const doc = editorInstance.state.doc.toJSON();
      const requiredSkillsIndex = doc.content.findIndex(
        (node: any) =>
          node.type === 'paragraph' &&
          node.content?.[0]?.text === 'Required Skills' &&
          node.content?.[0]?.marks?.some((mark: any) => mark.type === 'bold'),
      );

      if (requiredSkillsIndex >= 0) {
        let position = 0;
        for (let i = 0; i <= requiredSkillsIndex; i++) {
          const node = editorInstance.state.doc.child(i);
          position += node.nodeSize;
        }

        const bulletList = {
          type: 'bulletList',
          content: jobDescription.mandatorySkills.map((item) => ({
            type: 'listItem',
            content: [
              { type: 'paragraph', content: [{ type: 'text', text: item }] },
            ],
          })),
        };

        editorInstance.chain().focus().insertContentAt(position, bulletList).run();
      }
    }
  }, [editorInstance, jobDescription.mandatorySkills]);

  const insertBehavioralAttributes = useCallback(() => {
    if (
      editorInstance &&
      jobDescription.behavioralAttributes &&
      jobDescription.behavioralAttributes.length > 0
    ) {
      const doc = editorInstance.state.doc.toJSON();
      const BehavioralIndex = doc.content.findIndex(
        (node: any) =>
          node.type === 'paragraph' &&
          node.content?.[0]?.text === 'Behavioural Attributes' &&
          node.content?.[0]?.marks?.some((mark: any) => mark.type === 'bold'),
      );

      if (BehavioralIndex >= 0) {
        let position = 0;
        for (let i = 0; i <= BehavioralIndex; i++) {
          const node = editorInstance.state.doc.child(i);
          position += node.nodeSize;
        }

        const bulletList = {
          type: 'bulletList',
          content: jobDescription.behavioralAttributes.map((item) => ({
            type: 'listItem',
            content: [
              { type: 'paragraph', content: [{ type: 'text', text: item }] },
            ],
          })),
        };

        editorInstance.chain().focus().insertContentAt(position, bulletList).run();
      }
    }
  }, [editorInstance, jobDescription.behavioralAttributes]);

  const insertDesiredSkills = useCallback(() => {
    if (
      editorInstance &&
      jobDescription.desirableSkills &&
      jobDescription.desirableSkills.length > 0
    ) {
      const doc = editorInstance.state.doc.toJSON();
      const desiredSkillsIndex = doc.content.findIndex(
        (node: any) =>
          node.type === 'paragraph' &&
          node.content?.[0]?.text === 'Desired Skills' &&
          node.content?.[0]?.marks?.some((mark: any) => mark.type === 'bold'),
      );

      if (desiredSkillsIndex >= 0) {
        let position = 0;
        for (let i = 0; i <= desiredSkillsIndex; i++) {
          const node = editorInstance.state.doc.child(i);
          position += node.nodeSize;
        }

        const bulletList = {
          type: 'bulletList',
          content: jobDescription.desirableSkills.map((item) => ({
            type: 'listItem',
            content: [
              { type: 'paragraph', content: [{ type: 'text', text: item }] },
            ],
          })),
        };

        editorInstance.chain().focus().insertContentAt(position, bulletList).run();
      }
    }
  }, [editorInstance, jobDescription.desirableSkills]);

  const insertPerksAndBenefits = useCallback(() => {
    if (
      editorInstance &&
      orgPerksAndBenefits &&
      orgPerksAndBenefits.trim().length > 0
    ) {
      const doc = editorInstance.state.doc.toJSON();
      const perksIndex = doc.content.findIndex(
        (node: any) =>
          node.type === 'paragraph' &&
          node.content?.[0]?.text === 'Perks and Benefits' &&
          node.content?.[0]?.marks?.some((mark: any) => mark.type === 'bold'),
      );

      if (perksIndex >= 0) {
        let position = 0;
        for (let i = 0; i <= perksIndex; i++) {
          const node = editorInstance.state.doc.child(i);
          position += node.nodeSize;
        }

        const paragraphs = orgPerksAndBenefits
          .split(/\n\s*\n/)
          .map((block: string) => ({
            type: 'paragraph',
            content: [{ type: 'text', text: block.trim() }],
          }));

        editorInstance.chain().focus().insertContentAt(position, paragraphs).run();
      }
    }
  }, [editorInstance, orgPerksAndBenefits]);

  const insertCompanyDetails = useCallback(() => {
    if (
      editorInstance &&
      orgCompanyDetails &&
      orgCompanyDetails.trim().length > 0
    ) {
      const doc = editorInstance.state.doc.toJSON();
      const companyIndex = doc.content.findIndex(
        (node: any) =>
          node.type === 'paragraph' &&
          node.content?.[0]?.text === 'Company Details' &&
          node.content?.[0]?.marks?.some((mark: any) => mark.type === 'bold'),
      );

      if (companyIndex >= 0) {
        let position = 0;
        for (let i = 0; i <= companyIndex; i++) {
          const node = editorInstance.state.doc.child(i);
          position += node.nodeSize;
        }

        const paragraphs = orgCompanyDetails
          .split(/\n\s*\n/)
          .map((block: string) => ({
            type: 'paragraph',
            content: [{ type: 'text', text: block.trim() }],
          }));

        editorInstance.chain().focus().insertContentAt(position, paragraphs).run();
      }
    }
  }, [editorInstance, orgCompanyDetails]);

  const insertAll = useCallback(() => {
    if (!editorInstance) return;
    editorInstance.commands.setContent(JSON.parse(defaultJsonTemplate));
    setInitialEditorState(JSON.parse(defaultJsonTemplate));
    setJobPostingData((prev) => ({ ...prev, language: undefined }));
    insertResponsibilities();
    insertRequiredSkills();
    insertDesiredSkills();
    insertPerksAndBenefits();
    insertCompanyDetails();
    insertBehavioralAttributes();
    setIsImported(true);
  }, [
    editorInstance,
    setJobPostingData,
    insertResponsibilities,
    insertRequiredSkills,
    insertDesiredSkills,
    insertPerksAndBenefits,
    insertCompanyDetails,
    insertBehavioralAttributes,
  ]);

  const undoImports = useCallback(() => {
    if (editorInstance && initialEditorState) {
      editorInstance.commands.setContent(initialEditorState);
      setIsImported(false);
    }
  }, [editorInstance, initialEditorState]);

  const handleLoadTranslation = useCallback(
    (jsonTemplate: string, htmlTemplate: string, lang: string, autoSave = false) => {
      unstable_batchedUpdates(() => {
        setActiveJsonTemplate(jsonTemplate);
        setEditorKey((k) => k + 1);
        setJobPostingData((prev) => ({
          ...prev,
          language: lang || undefined,
        }));
        if (lang && autoSave) setAutoSaveLang(lang);
      });
    },
    [setJobPostingData],
  );

  const handleTranslationsChanged = useCallback(
    (langs: string[]) => {
      setJobPostingData((prev) => ({ ...prev, availableLanguages: langs }));
    },
    [setJobPostingData],
  );

  // Auto-save translation remount listener
  useEffect(() => {
    if (!autoSaveLang || !editorInstance || !resolvedPostId || autoSavingRef.current) return;
    const langToSave = autoSaveLang;
    setAutoSaveLang(null);
    autoSavingRef.current = true;

    const doSave = async () => {
      try {
        await new Promise<void>((resolve) => setTimeout(resolve, 80));
        const html = editorInstance.getHTML();
        const json = JSON.stringify(editorInstance.getJSON());
        const res = await saveTranslation(resolvedPostId, langToSave, {
          jsonTemplate: json,
          htmlTemplate: html,
        });
        if (res.ok) {
          setJobPostingData((prev) => ({
            ...prev,
            availableLanguages: (prev.availableLanguages ?? []).includes(langToSave)
              ? (prev.availableLanguages ?? [])
              : [...(prev.availableLanguages ?? []), langToSave],
          }));
          toast.success('Translation saved', { position: 'bottom-center' });
        }
      } catch (err) {
        console.error('auto-save error', err);
      } finally {
        autoSavingRef.current = false;
      }
    };

    doSave();
  }, [editorInstance, autoSaveLang, resolvedPostId, setJobPostingData]);

  const saveTemplate = useCallback(
    async (emailTemplate: string, emailJsonTemplate: string) => {
      try {
        const activeLang = jobPostingData.language;
        if (activeLang && activeLang !== 'en' && resolvedPostId) {
          await saveTranslation(resolvedPostId, activeLang, {
            jsonTemplate: emailJsonTemplate,
            htmlTemplate: emailTemplate,
          });
          setJobPostingData((prev) => ({
            ...prev,
            language: undefined,
            availableLanguages: (prev.availableLanguages ?? []).includes(activeLang)
              ? (prev.availableLanguages ?? [])
              : [...(prev.availableLanguages ?? []), activeLang],
          }));
          setActiveJsonTemplate(jobPostingData.jsonTemplate ?? defaultJsonTemplate);
          setEditorKey((k) => k + 1);
        } else {
          setJobPostingData({
            ...jobPostingData,
            htmlTemplate: emailTemplate,
            jsonTemplate: emailJsonTemplate,
          });
        }
      } catch (err) {
        console.error('saveTemplate error', err);
      }
    },
    [jobPostingData, resolvedPostId, setJobPostingData],
  );

  const saveCurrentTemplate = useCallback(async (): Promise<boolean> => {
    if (!editorInstance) return false;
    if (jobPostingData.language) return true;
    const html = editorInstance.getHTML();
    const json = JSON.stringify(editorInstance.getJSON());

    setJobPostingData((prev) => ({
      ...prev,
      htmlTemplate: html,
      jsonTemplate: json,
    }));

    try {
      const response = await updateJobPostUpload(
        resolvedPostId,
        id,
        jobPostingData.department,
        jobPostingData.jobTitle,
        jobPostingData.workType,
        json,
        html,
        toISODateString(jobPostingData.startDate),
        toISODateString(jobPostingData.endDate),
        jobPostingData.active,
        jobPostingData.location,
        jobPostingData.videoFilename instanceof File
          ? jobPostingData.videoFilename
          : undefined,
        jobPostingData.videoDescription,
        jobPostingData.videoTitle,
      );
      return response.ok;
    } catch (err) {
      console.error(err);
      return false;
    }
  }, [editorInstance, jobPostingData, resolvedPostId, id, setJobPostingData]);

  const getCurrentEditorContent = useCallback(() => {
    if (editorInstance) {
      return {
        html: editorInstance.getHTML(),
        json: JSON.stringify(editorInstance.getJSON()),
      };
    }
    return {
      html: jobPostingData.htmlTemplate ?? '',
      json: jobPostingData.jsonTemplate ?? activeJsonTemplate,
    };
  }, [editorInstance, jobPostingData, activeJsonTemplate]);

  const handleSaveOrCreate = useCallback(async () => {
    if (!editorInstance || isSaving) return;
    setIsSaving(true);

    const html = editorInstance.getHTML();
    const json = JSON.stringify(editorInstance.getJSON());

    try {
      const activeLang = jobPostingData.language;
      if (activeLang && activeLang !== 'en' && resolvedPostId) {
        const res = await saveTranslation(resolvedPostId, activeLang, {
          jsonTemplate: json,
          htmlTemplate: html,
        });

        if (res.ok) {
          toast.success(`Translation for ${activeLang} saved successfully!`, {
            position: 'bottom-center',
          });
          setJobPostingData((prev) => ({
            ...prev,
            language: undefined,
            availableLanguages: (prev.availableLanguages ?? []).includes(activeLang)
              ? (prev.availableLanguages ?? [])
              : [...(prev.availableLanguages ?? []), activeLang],
          }));
          setActiveJsonTemplate(jobPostingData.jsonTemplate ?? defaultJsonTemplate);
          setEditorKey((k) => k + 1);
        } else {
          throw new Error('Failed to save translation');
        }
      } else {
        if (resolvedPostId) {
          const res = await updateJobPostUpload(
            resolvedPostId,
            id,
            jobPostingData.department,
            jobPostingData.jobTitle,
            jobPostingData.workType,
            json,
            html,
            toISODateString(jobPostingData.startDate),
            toISODateString(jobPostingData.endDate),
            jobPostingData.active,
            jobPostingData.location,
            jobPostingData.videoFilename instanceof File
              ? jobPostingData.videoFilename
              : undefined,
            jobPostingData.videoDescription || undefined,
            jobPostingData.videoTitle || undefined,
          );

          if (res.ok) {
            setJobPostingData((prev) => ({
              ...prev,
              htmlTemplate: html,
              jsonTemplate: json,
            }));
            toast.success(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.toasts.jobPostUpdated) || 'Job Post updated successfully!', {
              position: 'bottom-center',
            });
          } else {
            throw new Error('Failed to update job post');
          }
        } else {
          const res = await createJobPostUpload(
            id,
            jobPostingData.department,
            jobPostingData.jobTitle,
            jobPostingData.workType,
            json,
            html,
            toISODateString(jobPostingData.startDate),
            toISODateString(jobPostingData.endDate),
            jobPostingData.active,
            jobPostingData.location,
            jobPostingData.videoFilename instanceof File
              ? jobPostingData.videoFilename
              : undefined,
            jobPostingData.videoDescription || undefined,
            jobPostingData.videoTitle || undefined,
          );

          if (res.ok) {
            const data = await res.json();
            setJobPostId(data.id);
            setJobPostingData((prev) => ({
              ...prev,
              id: data.id,
              htmlTemplate: html,
              jsonTemplate: json,
            }));
            setStages((prevStages: any[]) =>
              prevStages.map((stage) =>
                stage.label === 'Job Post'
                  ? { ...stage, status: 'In Progress' }
                  : stage,
              ),
            );
            toast.success('Job Post Created Successfully!', {
              position: 'bottom-center',
            });
          } else {
            throw new Error('Failed to create job post');
          }
        }
      }
    } catch (err) {
      toast.error('An error occurred while saving the job post.', {
        position: 'bottom-center',
      });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  }, [
    editorInstance,
    isSaving,
    jobPostingData,
    resolvedPostId,
    id,
    setJobPostingData,
    setJobPostId,
    setStages,
    t,
  ]);

  return {
    resolvedPostId,
    editorInstance,
    setEditorInstance,
    liveHtml,
    isPreviewOpen,
    setIsPreviewOpen,
    isSaving,
    variables,
    activeJsonTemplate,
    editorKey,
    isImported,
    insertAll,
    undoImports,
    handleLoadTranslation,
    handleTranslationsChanged,
    saveTemplate,
    saveCurrentTemplate,
    getCurrentEditorContent,
    handleSaveOrCreate,
    t,
  };
};
